import axios from 'axios';

import { API_HOST } from 'settings';
import { $FixTypeLater } from 'types';
import { handleLogout } from 'utils';

import { API_PATHS } from './apiPaths';
import { refreshToken } from './utils';

const httpClient = axios.create({
  baseURL: API_HOST,
  withCredentials: false,
  headers: {
    'Access-Control-Allow-Origin': '*',
    'Content-Type': 'application/json',
  },
});

let refreshCall: Promise<string> | null = null;
let pendingRequests: Array<(token: string) => void> = [];

httpClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 errors on endpoints that are not excluded
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      originalRequest.url !== '/calendar/google/events' &&
      originalRequest.url !== API_PATHS.AUTH.LOGIN() &&
      originalRequest.url !== API_PATHS.AUTH.REFRESH_TOKEN()
    ) {
      originalRequest._retry = true;

      // If there's no refresh call in progress, start one and store the promise.
      if (!refreshCall) {
        refreshCall = refreshToken(httpClient).finally(() => {
          refreshCall = null;
        });
      }

      try {
        // Await the shared refresh call for a new access token.
        const newAccessToken = await refreshCall;

        // Update the original request with the new token.
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        // Resolve any pending requests with the new token.
        pendingRequests.forEach((cb) => cb(newAccessToken));
        pendingRequests = [];

        // Retry the original request.
        return httpClient.request(originalRequest);
      } catch (_error: $FixTypeLater) {
        pendingRequests = [];
        handleLogout();
        return Promise.reject(_error);
      }
    }

    return Promise.reject(error);
  },
);

export { httpClient };
